import { computePosition, flip, shift, offset, arrow, autoUpdate, limitShift } from '@floating-ui/dom';
import { supportsMouseInput, supportsTouchInput } from './support';

const defaultOptions = {
  text: '',
  placement: 'top',
  follow: false,
  padding: 5,
  trigger: 'hover',
  arrow: false,
  destroyOnHide: true,
  container: document.body
};

// @todo support a container option to append tooltips to container
// @todo support a viewport option to specify the boundary container

const tooltips = new WeakMap();

function randomId (length = 5) {
  return Math.random().toString(36).substring(2, length + 2);
}

function delay (fn, ms) {
  return function () {
    setTimeout(fn, ms);
  };
}

function createOptions ($target, options) {
  options = Object.assign({
    container: $target.dataset.tooltipContainer || defaultOptions.container,
    text: $target.title || $target.dataset.tooltipText || defaultOptions.text,
    placement: $target.dataset.tooltipPlacement || defaultOptions.placement,
    follow: $target.dataset.tooltipFollow || defaultOptions.follow,
    padding: $target.dataset.tooltipPadding || defaultOptions.padding,
    trigger: $target.dataset.tooltipTrigger || defaultOptions.trigger,
    arrow: $target.hasAttribute('data-tooltip-arrow') || defaultOptions.arrow,
    destroyOnHide: $target.hasAttribute('data-tooltip-destroyOnHide') || defaultOptions.destroyOnHide
  }, options);

  if (typeof options.container === 'string') {
    options.container = document.querySelector(options.container);
  }

  return options;
}

export function destroyTooltip ($target) {
  if (tooltips.has($target)) {
    tooltips.get($target).destroy();
  }
}

export function createTooltip ($target, options = {}) {
  if (tooltips.has($target)) {
    return tooltips.get($target);
  }

  options = createOptions($target, options);

  const tooltipId = randomId();

  const $tooltip = document.createElement('div');
  // $tooltip.id = `iono-tooltip-${tooltipAutoIncrement++}`;
  $tooltip.id = `iono-tooltip-${tooltipId}`;
  $tooltip.className = 'iono-tooltip';
  $tooltip.innerHTML = options.text;
  $tooltip.setAttribute('role', 'tooltip');
  $tooltip.setAttribute('hidden', true);

  $target.removeAttribute('title');
  $target.setAttribute('data-tooltip', tooltipId);
  $target.setAttribute('aria-describedby', $tooltip.id);

  if (options.container) {
    options.container.insertAdjacentElement('beforeend', $tooltip);
  } else {
    $target.insertAdjacentElement('afterend', $tooltip);
  }

  let offsetX = null;
  let offsetY = null;
  let cleanup = function () {};
  const padding = parseInt(options.padding || 0, 10);

  const middleware = [
    shift({
      boundary: options.container,
      limiter: limitShift(padding)
    }),
    flip({
      boundary: options.container,
      fallbackAxisSideDirection: ['end', 'start']
    }),
    offset(padding)
  ];

  let $arrow;
  if (options.arrow) {
    $arrow = document.createElement('div');
    $arrow.className = 'iono-tooltip-arrow';
    $tooltip.appendChild($arrow);
    middleware.push(arrow({ element: $arrow }));
  }

  let clientX = 0;
  let clientY = 0;
  let referenceElement = $target;

  if (options.follow) {
    referenceElement = {
      getBoundingClientRect () {
        const domRect = $target.getBoundingClientRect() || {
          width: 0,
          height: 0,
          x: 0,
          y: 0
        };

        const _clientX = Math.max(domRect.left, Math.min(clientX, domRect.right));
        const _clientY = Math.max(domRect.top, Math.min(clientY, domRect.bottom));

        const isXAxis = options.follow === 'x' || options.follow === 'both';
        const isYAxis = options.follow === 'y' || options.follow === 'both';

        let width = domRect.width;
        let height = domRect.height;
        let x = domRect.x;
        let y = domRect.y;

        if (offsetX == null && _clientX && isXAxis) {
          offsetX = domRect.x - _clientX;
        }

        if (offsetY == null && _clientY && isYAxis) {
          offsetY = domRect.y - _clientY;
        }

        x -= offsetX || 0;
        y -= offsetY || 0;
        width = 0;
        height = 0;

        width = options.follow === 'y' ? domRect.width : 0;
        height = options.follow === 'x' ? domRect.height : 0;
        x = isXAxis && _clientX != null ? _clientX : x;
        y = isYAxis && _clientY != null ? _clientY : y;

        return {
          width,
          height,
          x,
          y,
          top: y,
          right: x + width,
          bottom: y + height,
          left: x
        };
      }
      // contextElement: $target
    };
  }

  function updatePosition () {
    computePosition(referenceElement, $tooltip, {
      placement: options.placement,
      middleware: middleware
    }).then(({ x, y, placement, middlewareData }) => {
      Object.assign($tooltip.style, {
        left: `${x}px`,
        top: `${y}px`
      });

      if (options.arrow) {
        const side = placement.split('-')[0];

        const staticSide = {
          top: 'bottom',
          right: 'left',
          bottom: 'top',
          left: 'right'
        }[side];

        if (middlewareData.arrow) {
          const { x, y } = middlewareData.arrow;
          Object.assign($arrow.style, {
            left: x != null ? `${x}px` : '',
            top: y != null ? `${y}px` : '',
            [staticSide]: `${-$arrow.offsetWidth / 2}px`,
            transform: 'rotate(45deg)'
          });
        }
      }
    });
  }

  function follow (event) {
    event = (typeof TouchEvent !== 'undefined' && event instanceof TouchEvent ? event.targetTouches[0] : event);

    clientX = event.clientX;
    clientY = event.clientY;

    updatePosition(referenceElement);
  }

  function show (event = null) {
    if ($tooltip.hasAttribute('hidden')) {
      $tooltip.removeAttribute('hidden');

      if (options.follow) {
        event && follow(event);

        if (supportsMouseInput()) {
          $target.addEventListener('mousemove', follow, { passive: true });
        }
        if (supportsTouchInput()) {
          $target.addEventListener('touchmove', follow, { passive: true });
        }
      }

      cleanup = autoUpdate(referenceElement, $tooltip, updatePosition);
    }
  }

  function hide () {
    $tooltip.setAttribute('hidden', true);

    if (options.follow) {
      if (supportsMouseInput()) {
        $target.removeEventListener('mousemove', follow, { passive: true });
      }
      if (supportsTouchInput()) {
        $target.removeEventListener('touchmove', follow, { passive: true });
      }
    }

    cleanup();

    if (options.destroyOnHide) {
      destroyTooltip($target);
    }
  }

  function update (newOptions = {}) {
    options = createOptions($target, newOptions);

    // @todo handle update of trigger option
    // @todo handle update of padding option
    // @todo handle update of container option
    // @todo handle update of viewport option

    $tooltip.innerHTML = options.text;
    options.arrow && $tooltip.appendChild($arrow);
    updatePosition();
    // cleanup();
  }

  const triggers = options.trigger.split(',');
  const triggerEvents = [];

  if (triggers.includes('hover')) {
    if (supportsMouseInput()) {
      triggerEvents.push(['mouseenter', show]);
      triggerEvents.push(['mouseleave', hide]);
    }
    if (supportsTouchInput()) {
      triggerEvents.push(['touchstart', show]);
      triggerEvents.push(['touchend', delay(hide, 500)]);
      triggerEvents.push(['touchcancel', hide]);
    }
  }

  if (triggers.includes('focus')) {
    triggerEvents.push(['focus', show]);
    triggerEvents.push(['blur', delay(hide, 500)]);
  }

  triggerEvents.forEach(([event, listener]) => {
    $target.addEventListener(event, listener);
  });

  function destroy () {
    cleanup();
    triggerEvents.forEach(([event, listener]) => {
      $target.removeEventListener(event, listener);
    });
    $target.removeAttribute('aria-describedby');
    $target.removeAttribute('data-tooltip');
    $target.setAttribute('title', options.text);
    $tooltip.remove();
    tooltips.delete($target);
  }

  const tooltip = {
    show,
    hide,
    update,
    destroy
  };

  tooltips.set($target, tooltip);

  return tooltip;
}

export function updateTooltip ($target, options = {}) {
  const tooltip = tooltips.get($target);

  if (tooltip) {
    tooltip.update(options);
  } else if (options.text) {
    $target.setAttribute('title', options.text);
  }
}
